import { GenericChoiceTailored } from 'resources/catalog/packages/ProductPackageBaseFormContent'
import { include, intersect } from 'resources/operations/events/utils'
import { Location } from 'services/api/entities/catalog/location/types/internal/location-models'
import {
  BaseProductPackage,
  OptionedPackage,
  ProductPackage,
  ProductPreset,
} from 'services/api/entities/catalog/packages/common'
import { APIDrinkPackage } from 'services/api/entities/catalog/packages/drinkPackage'
import {
  EventPackage,
  EventPackagePackageKeys,
} from 'services/api/entities/catalog/packages/eventPackage'
import { FoodPackage } from 'services/api/entities/catalog/packages/foodPackage'
import {
  LocationPackage,
  LocationPreset,
} from 'services/api/entities/catalog/packages/locationPackage'
import { getById, getId, isDefined, keys } from 'services/utils'
import {
  containsRelations,
  getMaxOfMinQuantity,
  getMinOfMaxQuantity,
  isPkgPublic,
} from 'services/utils/validation'
import { WithId, WithIdAndName, WithMinAndMaxQuantity } from 'types/common'
export type WithCustomerAndOrganizationIDS = {
  customers: number[]
  organizations: number[]
}
export type ProductPackageWithMinAndMaxQuantity<
  T extends ProductPreset = ProductPreset
> = WithIdAndName &
  Partial<WithMinAndMaxQuantity> & {
    presets: T[]
  } & WithCustomerAndOrganizationIDS

export type OnlyWithPresetsOrId<T> = { presets: T[] } | number

type LocationPresetProductTuple = [LocationPreset, Location]

export type PackagesWithMinAndMaxQuantityMap = Record<
  EventPackagePackageKeys,
  {
    packages: ProductPackageWithMinAndMaxQuantity[]
    products: GenericChoiceTailored[]
  }
>

type InCreate<T extends ProductPackage> = Omit<T, 'products'> & {
  products?: T['products'] | undefined[]
}

type EventPackagesInCreationPackage =
  | InCreate<BaseProductPackage>
  | InCreate<OptionedPackage>
  | InCreate<FoodPackage>
  | InCreate<LocationPackage>
  | InCreate<APIDrinkPackage>

type EvenPackageKeyFormPackageTuple = [
  EventPackagePackageKeys,
  EventPackagesInCreationPackage
]
type CatalogProduct = WithId &
  //TODO: remove partial if unecessary, added to prototype fasten type checking
  Partial<WithMinAndMaxQuantity> &
  WithCustomerAndOrganizationIDS
// >
const getCustomerAndOrganization = (catalogProducts: CatalogProduct[]) => {
  const privatesWithCustomers = catalogProducts.filter(
    (c) => !!c.customers.length
  )
  const privatesWithOrganizations = catalogProducts.filter(
    (c) => !!c.organizations.length
  )
  const organizations = intersect(
    privatesWithOrganizations.map((c) => c.organizations)
  )
  // const organizations = organizationsInteresct.map((o) => o.id)
  const customers = intersect(privatesWithCustomers.map((p) => p.customers))
  // const customers = customersIntersect.map((c) => c.id)

  return {
    customers,
    // customersFull: customersIntersect,
    organizations,
    // organizationsFull: organizationsInteresct,
  }
}

export const getPackageWithMinAndMaxQuantity =
  <
    T extends ProductPreset = ProductPreset,
    S extends CatalogProduct = CatalogProduct
  >(
    products: S[]
  ) =>
  (
    pkg: InCreate<ProductPackage<T>>
  ): ProductPackageWithMinAndMaxQuantity<T> => {
    const presets = (
      (pkg.products as (T | undefined)[] | undefined) ?? []
    ).filter(isDefined) as T[]

    const catalogProducts = include(presets.map(getId), products)
    const {
      customers,
      // customersFull,
      organizations,
      // organizationsFull,
    } = getCustomerAndOrganization(catalogProducts)
    const catalogPackage = {
      id: pkg.id,
      name: pkg.name,
      minQuantity: getMaxOfMinQuantity(catalogProducts),
      maxQuantity: getMinOfMaxQuantity(catalogProducts),
      presets,
      customers,
      // customersFull, // TODO FIX: check if I need it.
      organizations,
      // organizationsFull, // TODO FIX: check if I need it.
    }
    return catalogPackage
  }

const getMaxQuantityFromLocationPackage = (
  presetProductTuple: LocationPresetProductTuple[]
) => {
  return Math.min(
    ...presetProductTuple.map(([preset, location]) => {
      const arrangement =
        preset.arrangement && location.arrangements[preset.arrangement]

      return !!arrangement?.capacity
        ? arrangement.capacity
        : location.peopleCapacity
    })
  )
}

export const getLocationPackageWithMinAndMaxQuantity =
  (products: Location[]) =>
  (
    pkg: InCreate<LocationPackage>
  ): ProductPackageWithMinAndMaxQuantity<LocationPreset> => {
    const presets = (
      (pkg.products as (LocationPreset | undefined)[] | undefined) ?? []
    ).filter(isDefined) as LocationPreset[]

    const catalogProducts: CatalogProduct[] = include(
      presets.map(getId),
      products
    )

    const presetProductTuples = presets
      .map((preset) => [preset, getById(catalogProducts, preset.id)])
      .filter(([, product]) =>
        isDefined(product)
      ) as LocationPresetProductTuple[]
    const { customers, organizations } =
      getCustomerAndOrganization(catalogProducts)

    return {
      id: pkg.id,
      name: pkg.name,
      maxQuantity: getMaxQuantityFromLocationPackage(presetProductTuples),
      presets,
      customers,
      organizations,
    }
  }

// ======================
// HELPER FUNCTIONS FOR VALIDATING THE EVENT PACKAGE FORM
// ======================

/**
 *
 * @param form The formData of the event package
 * @param packagesMap The map containing for each type of package, the list of already existing packages with constraints
 * and the list of products from the catalog to derive the constraints of in creation packages
 * @returns the list of packages with constraints
 */
export const getEventPackageFormAlreadyCreatedPackagesWithConstraints = (
  form: EventPackage,
  packagesMap: PackagesWithMinAndMaxQuantityMap
) =>
  keys(packagesMap)
    .map((pkgKey) => {
      const formPackage = form[pkgKey]
      if (typeof formPackage === 'number') {
        return getById(
          packagesMap[pkgKey].packages,
          formPackage as number | undefined
        )
      }
      return undefined
    })
    .filter(isDefined) as ProductPackageWithMinAndMaxQuantity[]

export const getEventPackageFormInCreationPackageTuples = (
  form: EventPackage,
  packagesMap: PackagesWithMinAndMaxQuantityMap
) =>
  keys(packagesMap)
    .map((pkgKey) => {
      const formPackage = form[pkgKey]
      if (
        typeof formPackage !== 'number' &&
        typeof formPackage !== 'undefined'
      ) {
        return [pkgKey, formPackage]
      }
      return undefined
    })
    .filter(isDefined) as [
    EventPackagePackageKeys,
    EventPackagesInCreationPackage
  ][]

export const getEventPackageFormInCreationPackageFromTuple = (
  packagesMap: PackagesWithMinAndMaxQuantityMap,
  [pkgKey, formPackage]: EvenPackageKeyFormPackageTuple
) => {
  const formPackagePresets = (
    (formPackage.products as (WithId | undefined)[] | undefined) ?? []
  ).filter(isDefined) as WithId[]

  const relatedProducts = include(
    formPackagePresets.map(getId),
    packagesMap[pkgKey].products
  )

  if (pkgKey !== 'locationPackage') {
    return getPackageWithMinAndMaxQuantity(relatedProducts)(formPackage)
  } else {
    return getLocationPackageWithMinAndMaxQuantity(
      relatedProducts as Location[]
    )(formPackage)
  }
}

export const getEventPackageFormInCreationPackagesWithConstraints =
  (packagesMap: PackagesWithMinAndMaxQuantityMap) =>
  (tuple: EvenPackageKeyFormPackageTuple) =>
    getEventPackageFormInCreationPackageFromTuple(packagesMap, tuple)

export const pkgByCustomersAndOrgs = <T extends GenericChoiceTailored>(
  packages: T[],
  eventPkgCustomers: number[],
  eventPkgOrgs: number[]
) =>
  packages.filter((pkg) => {
    if (isPkgPublic(pkg)) {
      return true
    }
    if (!eventPkgCustomers.length && !eventPkgOrgs.length) {
      return false
    }
    return containsRelations(pkg, {
      organizations: eventPkgOrgs,
      customers: eventPkgCustomers,
    })
  })
